import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import getStateManager from '../StateManager';
import { settingControls, useSavedSettings } from '../Tabs/settingFunctions';
import { useReceivedSettings, useTabController } from './useTabController';
import {
  applyFieldDraft,
  createFieldSettingsDraft,
  pruneFieldDraft,
  updateFieldDraft,
  retainSettingsReadback,
  settingsLeafPaths
} from '../Utils/settings_draft';
import {
  applyNutritionDraft,
  createNutritionDraftStore,
  getNutritionEditorSession,
  isNutritionField,
  loadNutritionDraft,
  nutritionSavePatch,
  nutritionScope,
  nutritionValues,
  normalizeNutritionValue,
  sendNutritionSave
} from '../Utils/nutrition_utils';

export function useNutritionSettings() {
  const { t } = useTranslation();
  const params = useParams();
  const stateManager = getStateManager();
  const { controller, parent, controllerSnapshot, parentSnapshot } = useTabController(params);
  const scope = nutritionScope(controller, parent);
  const nutrition = useReceivedSettings(parent, 'nutrition');
  // Submitted values remain visible for retries, separately from unsaved edits
  // and slider previews, including after the editor remounts during reconnect.
  const { submittedFields, previousBaseline } = getNutritionEditorSession(controller, parent);
  if (!previousBaseline.current) previousBaseline.current = nutritionValues(nutrition);
  const [values, setValues] = useState(() =>
    applyNutritionDraft(nutrition, submittedFields.current)
  );
  const displayedFields = useRef({});
  const preview = useRef({});
  // Submission confirmation spans page mounts; untouched readback retention
  // only applies to changes observed while this editor is open.
  const mountedBaseline = useRef(nutritionValues(nutrition));
  const awaitingRestore = useRef(false);
  const displayFields = useCallback(
    () => updateFieldDraft(submittedFields.current, Object.entries(displayedFields.current)),
    [submittedFields]
  );
  const draft = useMemo(() => {
    const fields = createFieldSettingsDraft(
      createNutritionDraftStore(controller, parent),
      () => applyNutritionDraft(parent.settings.nutrition, submittedFields.current),
      true
    );
    return {
      ...fields,
      remove: (paths) => {
        const baseline = nutritionValues(parent.settings.nutrition);
        submittedFields.current = updateFieldDraft(
          submittedFields.current,
          paths.map((path) => [path, _.get(baseline, path)]),
          baseline,
          true
        );
        fields.remove(paths);
      },
      // Keep invalid stale edits available for reset, but do not recreate
      // recipes removed by the controller while displaying a restored draft.
      restore: () => applyNutritionDraft(
        parent.settings.nutrition,
        updateFieldDraft(submittedFields.current, Object.entries(fields.read()))
      )
    };
  }, [controller, parent, submittedFields]);

  // Hydrate before useSavedSettings runs the existing restore/discard flow.
  useEffect(() => {
    loadNutritionDraft(controller, parent);
    awaitingRestore.current = !_.isEmpty(draft.read());
  }, [controller, parent, draft]);
  useEffect(() => {
    const baseline = nutritionValues(nutrition);
    const displayedBaseline = mountedBaseline.current;
    mountedBaseline.current = baseline;
    const previous = applyFieldDraft(previousBaseline.current, submittedFields.current);
    const writable = controller.access_rights?.nutrition?.write === true;
    if (writable && !awaitingRestore.current) {
      const displayed = applyFieldDraft(displayedBaseline, displayFields());
      const paths = settingsLeafPaths(displayed).filter(isNutritionField);
      (displayed.nutrient_settings ?? []).forEach((recipe, index) => {
        if (baseline.nutrient_settings[index] &&
            recipe.pumps?.length !== baseline.nutrient_settings[index].pumps?.length)
          paths.push(`nutrient_settings[${index}].pumps`);
      });
      const retained = retainSettingsReadback(displayed, displayedBaseline, baseline, paths);
      const edits = retained.changed.filter(([path, value]) =>
        !Object.prototype.hasOwnProperty.call(submittedFields.current, path) &&
        !_.isEqual(value, _.get(baseline, path)));
      if (edits.length) {
        displayedFields.current = updateFieldDraft(displayedFields.current, edits);
        draft.edit(edits);
      }
    }
    // As in Multi IO, retire a submission only when its field changes to the
    // requested value. Unrelated readback must not acknowledge a pending revert.
    submittedFields.current = pruneFieldDraft(
      submittedFields.current,
      baseline,
      previousBaseline.current
    );
    const editableBaseline = applyNutritionDraft(nutrition, submittedFields.current);
    displayedFields.current = pruneFieldDraft(displayedFields.current, editableBaseline, previous);
    draft.reconcile(editableBaseline, previous);
    previousBaseline.current = baseline;
    setValues(
      applyNutritionDraft(
        nutrition,
        writable ? updateFieldDraft(displayFields(), Object.entries(preview.current)) : {}
      )
    );
  }, [controller, controllerSnapshot.access_rights?.nutrition?.write, displayFields, draft, nutrition, previousBaseline, submittedFields]);

  let invalid = false;
  try {
    nutritionSavePatch(nutrition, parentSnapshot.settings.irrigation_tank_info, scope, displayFields());
  } catch (_) {
    invalid = true;
  }
  const controls = settingControls(controller, parent, 'nutrition', values, setValues, {
    draft,
    getBaseline: () => nutritionValues(parent.settings.nutrition),
    canEdit: () => Boolean(controller.access_rights?.nutrition?.write),
    canRestore: () => Boolean(controller.access_rights?.nutrition?.write),
    isEditablePath: isNutritionField,
    normalizeValue: normalizeNutritionValue,
    onChange: ({ type, entries = [], saveTemp }) => {
      awaitingRestore.current = false;
      if (type === 'restore') {
        displayedFields.current = _.cloneDeep(draft.read());
        preview.current = {};
      } else if (type === 'resetAll') {
        submittedFields.current = {};
        displayedFields.current = {};
        preview.current = {};
      } else if (type === 'edit' && !saveTemp) {
        entries.forEach(([path, value]) => {
          preview.current[path] = value;
        });
      } else {
        entries.forEach(([path]) => {
          delete preview.current[path];
        });
        displayedFields.current = updateFieldDraft(
          displayedFields.current,
          entries,
          type === 'reset'
            ? nutritionValues(parent.settings.nutrition)
            : applyNutritionDraft(parent.settings.nutrition, submittedFields.current),
          true
        );
      }
    },
    submit: () => {
      if (invalid || Object.keys(preview.current).length) return false;
      const fields = displayFields();
      if (!sendNutritionSave(stateManager, controller, parent, fields)) return false;
      submittedFields.current = fields;
      displayedFields.current = {};
      return true;
    }
  });
  // The restore/discard choice concerns unsaved edits only. The page Reset
  // above separately cancels both those edits and pending submissions.
  const loadSettingsControls = {
    ...controls,
    resetSettings: () => {
      awaitingRestore.current = false;
      draft.clear();
      displayedFields.current = {};
      preview.current = {};
      setValues(applyNutritionDraft(parent.settings.nutrition, submittedFields.current));
    }
  };
  const savedSettings = useSavedSettings(
    parent,
    loadSettingsControls,
    'nutrition',
    () => !_.isEmpty(draft.read())
  );
  return {
    t,
    controller,
    parent,
    controllerSnapshot,
    parentSnapshot,
    values,
    controls,
    loadSettingsControls,
    ...savedSettings,
    tankSettings: true,
    saveDisabled: invalid || Object.keys(preview.current).length > 0,
    invalid
  };
}
